import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';

import {combineLatest, Observable, Subscription} from 'rxjs';

import {Message, FilterService, SortEvent} from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import { Calendar } from 'primeng/calendar';
import { Table } from 'primeng/table';

import { DateTime } from 'luxon';

import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { SiteSubinventory, SubinventoryType } from '@shared/models/subinventory';
import { HistoryResponse } from '@shared/models/history';

import { BolService } from '../../services/bol.service';
import { OutboundLoadService } from '../../services/outbound-load.service';

import {OutboundLoad, OutboundLoadPartial} from '../../models/outbound-load';
import { ShiftContextService } from '../../services/shift-context.service';
import { Product } from '@shared/models/product';
import { ProductService } from '../../services/product.service';
import { OutboundLoadStatus } from '../../models/outbound-load-status';
import { OutboundLoadPartialUpdate } from '../../models/outbound-load-partial-update';
import {ShiftStatus, WorkedShift} from "@shared/models/worked-shift";
import {BlobStorageComponent} from "@shared/components/blob-storage/blob-storage.component";
import {LineItem} from "../../models/shift-validation/loads-summary";
import {OverlayPanel} from "primeng/overlaypanel";
import {DialogService} from "primeng/dynamicdialog";
import {
    CreateOutboundLoadFromTemplateComponent
} from "./components/create-outbound-load-from-template/create-outbound-load-from-template.component";
import {getSiteInfo, SiteInfo} from "@shared/helpers/site-info";

@Component({
    selector: 'outbound-loads',
    templateUrl: './outbound-loads.component.html',
    styleUrls: ['./outbound-loads.component.scss']
})
export class OutboundLoadsComponent implements OnInit, OnDestroy {
    protected readonly ShiftStatus = ShiftStatus;
    loads: OutboundLoad[];
    combinedLoads: OutboundLoad[];
    subscriptions: Subscription[];
    siteId: string;
    date: DateTime;
    shiftId: number;
    subinventories: SiteSubinventory[];
    siteInfo: SiteInfo;

    lineItems: LineItem[] = [];
    products: Product[] = [];
    showShipLoad: boolean = false;
    loadToShip: OutboundLoad = null;
    showCancelLoad: boolean = false;
    loadToCancel: OutboundLoad = null;
    showHistory: boolean = false;
    showMinimalHistory: boolean = true;
    historyLoad: OutboundLoad = null;
    loading = false;
    hasMoreRows: boolean = false;
    showBolReplaceDialog: boolean = false;
    replacementBol: { id: number, customerReferenceNumber: string };
    importedLoadBols: { id: number, customerReferenceNumber: string }[] = [];
    selectedLoad: { id: number, propakBolNumber: string };
    showFileStorageDialog: boolean = false;
    OutboundLoadStatus = OutboundLoadStatus;
    uploadLoad: OutboundLoad;
    working: boolean = false;

    cols: any[];
    searchFlag = true;

    searchResults: OutboundLoad[];
    history: HistoryResponse[];
    private _fieldsDirty = false;
    loadedDateCompare = (l: WorkedShift, r: WorkedShift) => {
        if (!l && r) return 1;
        if (l && !r) return -1;
        if (!l && !r) return 0;

        const left = l?.date;
        const leftShift = l?.shift.number;
        const right = r?.date;
        const rightShift = r?.shift.number;

        if (left == right) return leftShift < rightShift || leftShift == rightShift ? -1 : 1;
        else return left > right ? 1 : -1;
    };
    dragTarget: OutboundLoad;
    dropTarget: OutboundLoad;

    showCreateOutboundLoadTemplateDialog: boolean = false;


    get fieldsDirty(): boolean {
        return this.searchFieldsAreDirty();
    };

    set fieldsDirty(val: boolean) {
        this._fieldsDirty = val;
    }

    readonly _plannedLoadDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Planned Outbound Load Deleted',
        detail: ''
    };

    readonly _shipToMissingAddressMsg: Message = {
        severity: 'warn',
        summary: 'Ship To location missing address',
        detail: 'Select a different Ship To or contact WAM Support to correct the current one'
    };

    @ViewChild('searchBOL', { static: false }) searchBOL: ElementRef;
    @ViewChild('searchGlid', { static: false }) searchGlid: ElementRef;
    @ViewChild('searchShipTo', { static: false }) searchShipTo: ElementRef;
    @ViewChild('searchTrailer', { static: false }) searchTrailer: ElementRef;
    @ViewChild('searchLoadedDate', { static: false }) searchLoadedDate: Calendar;
    @ViewChild('searchCustomerRef', { static: false }) searchCustomerRef: ElementRef;
    @ViewChild('searchCarrier', { static: false }) searchCarrier: ElementRef;
    @ViewChild('searchPooler', { static: false }) searchPooler: ElementRef;
    @ViewChild('op', { static: false }) overlayPanel: OverlayPanel;
    @ViewChild('dt') dt: Table;
    @ViewChild(BlobStorageComponent) blobStorageComponent: BlobStorageComponent;


    constructor(
        private router: Router,
        private messageService: MessageService,
        private shiftContextService: ShiftContextService,
        private outboundLoadService: OutboundLoadService,
        private activityService: ActivityBlockingService,
        private bolService: BolService,
        private productService: ProductService,
        private filterService: FilterService,
        private dialogService: DialogService
    ) {
    }

    ngOnInit() {
        this.cols = [
            {
                field: 'customerReferenceNumber', header: 'Customer Ref #', ariaLabel: 'Sort by Ref #',
                ariaLabelAsc: 'Sort by Ref # Ascending', ariaLabelDesc: 'Sort by Ref # Descending'
            },
            {
                field: 'propakBOLNumber', header: 'Propak BOL #', ariaLabel: 'Sort by BOL #',
                ariaLabelAsc: 'Sort by BOL # Ascending', ariaLabelDesc: 'Sort by BOL # Descending'
            },
            {
                field: 'customerSite.chepGlobalLocationId', header: 'GLID - Customer', ariaLabel: 'Sort by GLID',
                ariaLabelAsc: 'Sort by GLID Ascending', ariaLabelDesc: 'Sort by GLID Descending'
            },
            {
                field: 'customerSite.partySiteName', header: 'Ship To', ariaLabel: 'Sort by Ship To',
                ariaLabelAsc: 'Sort by Ship To Ascending', ariaLabelDesc: 'Sort by Ship To Descending'
            },
            {
                field: 'trailer.trailerNumber', header: 'Trailer', ariaLabel: 'Sort by Trailer Number',
                ariaLabelAsc: 'Sort by Trailer Number Ascending', ariaLabelDesc: 'Sort by Trailer Number Descending'
            },
            {
                field: 'carrierName', header: 'Carrier', ariaLabel: 'Sort Carrier',
                ariaLabelAsc: 'Sort by Carrier Ascending', ariaLabelDesc: 'Sort by Carrier Descending'
            },
            {
                field: 'loadedWorkedShift.date', header: 'Loaded', ariaLabel: 'Sort by Loaded Date',
                ariaLabelAsc: 'Sort by Loaded Date Ascending', ariaLabelDesc: 'Sort by Loaded Date Descending', type: 'date'
            },
            {
                field: 'shippedWorkedShift.date', header: 'Shipped/Canceled', ariaLabel: 'Sort by Shipped Date',
                ariaLabelAsc: 'Sort by Shipped Date Ascending', ariaLabelDesc: 'Sort by Shipped Date Descending', type: 'date'
            },
            {
                field: 'pooler.accountName', header: 'Pooler', ariaLabel: 'Sort by Pooler',
                ariaLabelAsc: 'Sort by Pooler Ascending', ariaLabelDesc: 'Sort by Pooler Descending'
            },
            {
                field: 'Quantity', header: 'Quantity', ariaLabel: 'Sort by Quantity',
                ariaLabelAsc: 'Sort by Quantity Ascending', ariaLabelDesc: 'Sort by Quantity Descending'
            }
        ];


        const initHandle = this.activityService.StartWorking();

        this.subscriptions = [
            this.shiftContextService.CurrentContext.subscribe(context => {
                this.siteInfo = getSiteInfo(context.site);
                this.subinventories = context?.site.subinventories.filter(s => s.isActive
                    && (s.subinventoryType === SubinventoryType.ChepPlantPallets
                        || s.subinventoryType === SubinventoryType.ChepPalletStorage))
                    .sort((l, r) => l.subinventoryType === r.subinventoryType
                        ? l.name.localeCompare(r.name)
                        : l.subinventoryType - r.subinventoryType)
                this.siteId = context.site?.id;
                this.date = context.date;
                this.shiftId = context.shift?.id;

                this.getLoads();

                this.activityService.FinishWorking(initHandle);
            }),

            this.productService.CurrentProducts.subscribe(p => {
                this.products = p;
            })
        ];

        this.filterService.register('dateFilter', (value, filter): boolean => {
            if (value === undefined)
                return true;
            if (!filter)
                return true;

            const formattedDate = DateTime.fromISO(value);
            if (DateTime.isDateTime(filter)) {
                return +formattedDate === +filter;
            }
            value = formattedDate.toFormat('MM/dd/yyyy');
            return value.includes(filter);
        });
    }

    getLoads() {
        if (this.siteId && this.date && this.shiftId) {
            const refreshHandle = this.activityService.StartWorking();

            this.outboundLoadService.getOutboundLoads(this.siteId, this.date, this.shiftId).subscribe({
                next: (outbounds) => {
                    this.loads = outbounds;
                    this.combinedLoads =
                        outbounds.slice().sort((a, b) => this.loadedDateCompare(a.loadedWorkedShift, b.loadedWorkedShift));
                    this.activityService.FinishWorking(refreshHandle);
            }, error: () => this.activityService.FinishWorking(refreshHandle)
            });
        }
    }

    getProduct(productId: number) {
        return this.products.find(p => p.id === productId)?.itemDescription;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    startLoad() {
        this.router.navigateByUrl('/loads/outbound/new');
    }

    openCreateFromTemplateDialog() {
        this.showCreateOutboundLoadTemplateDialog = true;
    }

    editLoad(load: OutboundLoad) {
        this.router.navigateByUrl('/loads/outbound/' + load.id);
    }

    partialEditLoad(load: OutboundLoad) {
        this.loadToShip = load;
        this.showShipLoad = true;
    }

    cancelLoad(load: OutboundLoad) {
        this.loadToCancel = load;
        this.showCancelLoad = true;
    }

    printLoad(load: OutboundLoad) {
        this.bolService.DownloadBolPdf(load.id);
    }

    loadShipped(result: OutboundLoadPartial) {
        Object.assign(this.loadToShip, result);

        this.loadToShip = null;
    }

    loadCanceled(result: OutboundLoad) {
        this.loadToCancel.canceledWorkedShift = result.canceledWorkedShift;
        this.loadToCancel.outboundLoadStatusId = OutboundLoadStatus.Canceled;
        this.loadToCancel = null;
    }

    changeSearchStatus(dt: any) {
        if (this.searchFlag === false) {
            dt.reset();
            this.combinedLoads = this.loads.slice();
        }
    }

    searchLoad() {
        this.loading = true;
        const filters = <any>this.dt.filters.valueOf();
        this.outboundLoadService.searchOutboundLoads({
            siteId: this.siteId,
            propakBOLNumber: filters['propakBOLNumber'].value ?? '',
            glid: filters['customerSite.chepGlobalLocationId'].value ?? '',
            shipTo: filters['customerSite.partySiteName'].value ?? '',
            Pooler: filters['pooler.accountName'].value ?? '',
            trailerNumber: filters['trailer.trailerNumber'].value ?? '',
            customerReferenceNumber: filters['customerReferenceNumber'].value ?? '',
            carrierScac: filters['carrierName'].value ?? '',
            loadedDate: filters['loadedWorkedShift.date'].value ?? ''
        }).subscribe(searched => {
            if (!!searched) {
                this.searchResults = [...searched.resultSet];
                this.hasMoreRows = searched.hasMoreRows;
                if (searched.resultSet.length > 0) {
                    const filterDuplicate = searched.resultSet.filter(f => !this.loads.find(x => x.id === f.id));
                    this.combinedLoads = [...filterDuplicate, ...this.loads];
                }
                this.loading = false;
            } else {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'No Loads Found',
                    detail: 'Refine Search Criteria and try again.'
                });
                this.loading = false;
            }
        });
    }

    filterTable(dt: Table, event: Event, field: string, filter: string = 'contains') {
        dt.filter((<HTMLInputElement>event.target).value, field, filter);
    }

    searchFieldsAreDirty(): boolean {
        if (!!this.searchBOL)
            return this._fieldsDirty = this.searchBOL.nativeElement.value.length >= 3 ||
                this.searchTrailer.nativeElement.value.length >= 3 ||
                this.searchCustomerRef.nativeElement.value.length >= 3 ||
                this.searchGlid.nativeElement.value.length >= 3 ||
                this.searchShipTo.nativeElement.value.length >= 3 ||
                this.searchPooler.nativeElement.value.length >= 3 ||
                this.searchLoadedDate.value;
        else
            return false;
    }

    fromSearch(id) {
        if (!!this.searchResults)
            return !!this.searchResults.find(sr => sr.id === id);
        else
            return false;
    }

    parseDate(date: Date) {
        return DateTime.fromJSDate(date);
    }

    filterText(text: string) {
        if (!text || text.length === 0)
            this.dt.reset();
        else
            this.dt.filter(text, 'loadedWorkedShift.date', 'dateFilter');

    }

    filterProduct(text: string) {
        if (!text || text.length === 0) {
            this.dt.reset();
        } else {
            // var filterProducts = this.products.find(p => p.itemDescription.includes(text));
            this.dt.filter(text, 'outboundLoadLineItems.0.product.itemDescription', 'contains');
        }
    }

    showHistoryDialog(load: OutboundLoad) {
        this.historyLoad = load;
        this.outboundLoadService.getHistory(this.siteId, load.id).subscribe(
            {
                next: (h) => {
                    if (h) {
                        this.history = h
                        this.showHistory = true;
                    }
                },
                error: () => this.showHistory = false
            })
    }

    get filteredHistory(): HistoryResponse[] {
        if (!this.history) {
            return [];
        }
        if (!this.showMinimalHistory) {
            return this.history;
        }
        return this.history.filter(record =>
            record.dataType !== "Trailer"
            || (
                record.action === 'add'
                || (
                    record.action === 'update'
                    && Object.hasOwn(record.history, "TrailerNumber")
                )
            )
        );
    }

    customSortEvent(event: SortEvent) {
        event.data.sort((a, b) => event.order * this.loadedDateCompare(a.loadedWorkedShift, b.loadedWorkedShift));
    }

    matchVendorBols(val: any) {
        this.importedLoadBols =
            this.combinedLoads.filter(l => l.outboundLoadStatusId === OutboundLoadStatus.Planned &&
                l.customerReferenceNumber?.includes(val.query)).map(l => {
                return ({id: l.id, customerReferenceNumber: l.customerReferenceNumber});
            });
    }


    replaceLoad(load: OutboundLoad) {
        this.selectedLoad = {id: load.id, propakBolNumber: load.propakBOLNumber};
        this.showBolReplaceDialog = true;
    }

    confirmBolReplace(event: Event) {
        this.working = true;
        this.outboundLoadService.replaceBol(this.selectedLoad.id, this.replacementBol.id).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Customer Reference Replaced',
                    detail: `The BOL ${this.selectedLoad.propakBolNumber} Customer ref# was replaced with ${this.replacementBol.customerReferenceNumber}.`
                });
                this.showBolReplaceDialog = false;
                this.selectedLoad = null;
                this.replacementBol = null;
                this.getLoads();
                this.working = false;
        }, error: () => this.working = false });
    }

    uploadDocument(load: OutboundLoad) {
        this.uploadLoad = load;
        this.showFileStorageDialog = true;

    }

    onShow() {
        if (this.uploadLoad == null)
            this.blobStorageComponent.getFiles();
    }
    onHide() {
        this.showFileStorageDialog = false;
        this.uploadLoad = null;
        this.blobStorageComponent.documentIds = [];
    }

    dropHandler(event: DragEvent) {
        event.preventDefault();
        this.working = true;
        this.dropTarget = this.dragTarget;

        const fileResultObs = (Array.from(event.dataTransfer.items)?.filter(i => i.kind === 'file')
            .map(i => i.getAsFile()) ?? Array.from(event.dataTransfer.files))
            .map((file, i) => this.blobStorageComponent.dragOverFunction(file, this.siteId, this.dropTarget.id, 'OutboundLoads'));

        combineLatest(fileResultObs).subscribe({
            next: (fileIds) => {
                this.dropTarget.documentIds.push(...fileIds);
                this.working = false;
            },
            error: () => {
                this.working = false; this.dropTarget = null;
            }
        });
        this.dragLeaveHandler();
    }

    dragOverHandler(event, load: OutboundLoad) {
        event.preventDefault();
        this.dragTarget = load;
    }

    dragLeaveHandler() {
        this.dragTarget = null;
    }

    dragged(id): boolean {
        return !!this.dragTarget && this.dragTarget.id === id;
    }

    dropped(id) {
        return !!this.dropTarget && this.dropTarget.id === id;
    }

    totalQuantity(lineItems: LineItem[]): number {
        return lineItems.map(x => x.quantity).reduce((val, tot) => val + tot, 0);
    }

    overlayToggle(event: any, lineItems: LineItem[]) {
        this.lineItems = lineItems;
        this.overlayPanel.show(event);
    }

    sendNotification(load: OutboundLoad) {
        this.working = true;
        this.outboundLoadService.resendNotification(load.id).subscribe({
            next: _ => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Loaded Notifications Resent',
                    detail: `Loaded Notifications for BOL ${load.propakBOLNumber} have been resent.`
                });
                this.working = false;
            },
            error: () => {
                this.working = false;
            }
        });
    }
}
